import { useEffect, useState } from 'react';

/**
 * Where a patient's diet-restrictions / motivations tags are shown:
 *
 *   • 'header'  — a compact full-width strip directly under the patient name
 *                 in the global header, spanning across to "Log out" (the
 *                 default).
 *   • 'sidebar' — inside the patient rail's Details section (the prior layout).
 *
 * Per-device (not per-profile), like Theme / Text size / Meal-photo size — a
 * clinician's screen real estate drives this, not the patient. Persists to
 * localStorage and broadcasts changes so the Profile switcher and an open
 * patient view (rail + header) stay in sync without a route change.
 */

export type TagPlacement = 'sidebar' | 'header';

const STORAGE_KEY = 'ui.tagPlacement';
const DEFAULT_PLACEMENT: TagPlacement = 'header';
const CHANGE_EVENT = 'rxf:tagplacementchange';

const isPlacement = (v: unknown): v is TagPlacement => v === 'sidebar' || v === 'header';

export const readTagPlacement = (): TagPlacement => {
  if (typeof window === 'undefined') return DEFAULT_PLACEMENT;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (isPlacement(raw)) return raw;
  } catch {
    /* private mode — fall through */
  }
  return DEFAULT_PLACEMENT;
};

const writeStored = (value: TagPlacement) => {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* ignore */
  }
};

export const useTagPlacement = (): [TagPlacement, (next: TagPlacement) => void] => {
  const [placement, setPlacementState] = useState<TagPlacement>(() => readTagPlacement());

  // Keep every mounted consumer in sync: the Profile switcher and the patient
  // view (rail + header strip) can be alive at once, and a `storage` event
  // covers other tabs. Re-read on any signal rather than trusting the payload.
  useEffect(() => {
    const sync = () => setPlacementState(readTagPlacement());
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const setPlacement = (next: TagPlacement) => {
    setPlacementState(next);
    writeStored(next);
    window.dispatchEvent(new Event(CHANGE_EVENT));
  };

  return [placement, setPlacement];
};
